import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { Project } from 'src/app/model/project/project';
import ProjectInvoice from 'src/app/model/invoice/project-invoice';
import Product from 'src/app/model/product/product';
import { ProductTypes } from 'src/app/model/product/product-type';
import { RequestService } from 'src/app/services/request.service';
import { SnackService } from 'src/app/services/snack.service';
import BrowserHelper from '../../../../helpers/browser-helper';
import { LoadingService } from 'src/app/services/loading.service';
import InvoiceHelper from 'src/app/helpers/invoice-helper';
import { SubProject } from '../../../../model/project/sub-project';
import { BaseProject } from '../../../../model/project/base-project';
import { Subject } from 'rxjs';
import { StringHelper } from 'src/app/helpers/string-helper';
import Company from 'src/app/model/company/company';
import { BoardPartAddedEvent } from 'src/app/events/board-part-added';
import { StandardStickerPartAddedEvent } from 'src/app/events/standard-sticker-part-added';
import { StandardStickerPartDeletedEvent } from 'src/app/events/standard-sticker-part-deleted';
import { SendQuotationMailComponent } from 'src/app/modals/send-quotation-mail/send-quotation-mail.component';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import RegisteredAction from 'src/app/model/registered-action';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import OtherProduct from 'src/app/model/product/other-product/other-product';
import * as globals from '../../../../../globals/globals';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { EmployeeChoicesService } from 'src/app/services/employee-choices.service';
import Employee from 'src/app/model/product/employee';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import Quotation from 'src/app/model/quotation';

@Component({
  selector: 'app-invoicing',
  templateUrl: './invoicing.component.html',
  styleUrls: ['./invoicing.component.scss'],
})
export class InvoicingComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  isCollapsed = false;
  safari: boolean;
  init = false;
  anySentToTeamleader = false;

  canSendInvoices = false;
  sendingInvoices = false;

  quotationHandlerChoices: Employee[] = [];

  private dialogRef: MatDialogRef<SendQuotationMailComponent>;

  constructor(
    public requestService: RequestService,
    public snackService: SnackService,
    public loadingService: LoadingService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private employeeChoicesService: EmployeeChoicesService,
    private dialog: MatDialog
  ) {
    super();
  }

  @Input()
  public project: Project;

  @Input()
  public inputProject: SubProject;

  @Input()
  public extraInfoPlaceholder: string;

  @Input()
  public involvedCompanies: Company[];

  @Input()
  public updateEmitter: EventEmitter<any>;

  @Input()
  public productAdded: Subject<Product>;

  @Input()
  public boardPartAdded: Subject<BoardPartAddedEvent>;

  @Input()
  public standardStickerPartAdded: Subject<StandardStickerPartAddedEvent>;

  @Input()
  public standardStickerPartDeleted: Subject<StandardStickerPartDeletedEvent>;

  @Input()
  public invoiceSent: Subject<any>;

  ngOnInit() {
    this.safari = BrowserHelper.isSafari();
    this.getQuotationHandlerChoices();
  }

  getProductLabel(product: Product, i: number) {
    if (this.isWindowSticker(product)) {
      return ProductTypes.WINDOWSTICKER.label + ' - #' + (i + 1);
    }
    if (this.isCustomWindowSticker(product)) {
      return ProductTypes.CUSTOMWINDOWSTICKER.label + ' - #' + (i + 1);
    }
    if (this.isBoard(product)) {
      return ProductTypes.BOARD.label + ' - #' + (i + 1);
    }
    if (this.isOtherProduct(product)) {
      return (
        ProductHelper.getType(
          product.type,
          (<OtherProduct>product).otherProductName
        ) +
        ' - #' +
        (i + 1)
      );
    }
  }

  getQuotationHandlerChoices() {
    this.addSubscription(
      this.employeeChoicesService.getQuotationHandlerChoicesSubscribable(),
      (quotationHandlers: Employee[]) => {
        this.quotationHandlerChoices = quotationHandlers;
      }
    );
  }

  isWindowSticker(product: Product) {
    return product.type === ProductTypes.WINDOWSTICKER.value;
  }

  isCustomWindowSticker(product: Product) {
    return product.type === ProductTypes.CUSTOMWINDOWSTICKER.value;
  }

  isBoard(product: Product) {
    return product.type === ProductTypes.BOARD.value;
  }

  isOtherProduct(product: Product) {
    return product.type === ProductTypes.OTHER_PRODUCT.value;
  }

  setNeedsQuotation(value: boolean) {
    this.inputProject.needsQuotation = value;
    if (value && this.inputProject.quotation == null) {
      this.inputProject.quotation = new Quotation();
    }
    this.updateEmitter.emit();
  }

  quotationHandlerChange() {
    this.updateEmitter.emit();
  }

  quotatationByDateChange() {
    this.updateEmitter.emit();
  }

  setImportantQuotation() {
    if (this.inputProject.quotationImportant != null) {
      this.inputProject.quotationImportant =
        !this.inputProject.quotationImportant;
    } else {
      this.inputProject.quotationImportant = true;
    }
    this.updateEmitter.emit();
  }

  quotationCommentsChange(value: string) {
    this.inputProject.quotationComments = value;
    this.updateEmitter.emit();
  }

  includeRepoInPriceChange(value: boolean) {
    (<BaseProject>this.inputProject).includeRepoInPrice = value;
    this.updateEmitter.emit();
  }

  extraInfoInvoiceChange(event, index) {
    this.inputProject.extraInfoInvoice = event;
    this.updateEmitter.emit();
  }

  placementInfoInvoiceChange(event, index) {
    this.project.products[index].productPlacement.placementInfoInvoice = event;
    this.updateEmitter.emit();
  }

  installerFeedbackChange(event, index) {
    this.project.products[index].productPlacement.installerFeedback = event;
    this.updateEmitter.emit();
  }

  public sendInvoices(sendToTeamleader: boolean) {
    this.sendingInvoices = true;
    this.requestService
      .post(
        { id: this.inputProject.id, sendToTeamleader: sendToTeamleader },
        '/subprojects/invoiceProject/' + this.getInvoiceType()
      )
      .subscribe((res) => {
        this.inputProject.status = res.entity.status;
        this.inputProject.projectInvoices = res.entity.projectInvoices;
        if (res.success) {
          this.anySentToTeamleader = true;
          this.invoiceSent.next();
          this.projectDisplayObservableService
            .getInvoiceSentSubscribable()
            .next();
          this.snackService.showSuccess(
            sendToTeamleader
              ? res.message
              : 'Aanmaak factuur succesvol gesimuleerd'
          );
        } else {
          this.snackService.showError(res.message);
        }
        this.sendingInvoices = false;
      });
  }

  public getInvoiceType() {
    return 'ProjectInvoice';
  }

  public invoiceMatchesType(projectInvoice: ProjectInvoice) {
    if (projectInvoice.type === 'PlacementInvoice') {
      return this.getInvoiceType() === 'PlacementInvoice';
    }
  }

  protected getMatchingInvoices(): ProjectInvoice[] {
    if (this.inputProject.projectInvoices == null) {
      return null;
    }
    return this.inputProject.projectInvoices.filter((i) =>
      this.invoiceMatchesType(i)
    );
  }

  public needsInvoice() {
    if (this.getInvoiceType() === 'PlacementInvoice') {
      return this.placementNeedsInvoice();
    }
    if (this.getInvoiceType() === 'Verkocht/Verhuurd') {
      return this.soldRentedNeedsInvoice();
    }
    if (this.getInvoiceType() === 'Afhaling') {
      return this.repoNeedsInvoice();
    }
  }

  protected placementNeedsInvoice(): boolean {
    return this.inputProject.withPlacement
      ? this.checkAllExecuted('productPlacement', 'executionComplete')
      : true;
  }

  protected soldRentedNeedsInvoice(): boolean {
    return this.checkAllExecuted('soldRented', 'executed');
  }

  protected repoNeedsInvoice(): boolean {
    return this.checkAllExecuted('repo', 'executed');
  }

  private checkAllExecuted(
    placementProp: string,
    executedProp: string
  ): boolean {
    if (this.project.products == null || this.project.products.length === 0) {
      return false;
    }
    for (let product of this.project.products) {
      if (
        product[placementProp] == null ||
        product[placementProp][executedProp] == null ||
        !product[placementProp][executedProp]
      ) {
        return false;
      }
    }
    return true;
  }

  public needsInvoiceChange(value) {
    this.inputProject.needsInvoice = value;
    this.updateEmitter.emit();
  }

  public canInvoiceChecked(product: Product) {
    return product.readyToInvoice;
  }

  public readyToInvoiceChange(value, index) {
    this.project.products[index].readyToInvoice = value.checked;
    this.canSendInvoices = this.project.products.every((p) => {
      return p.readyToInvoice;
    });
    this.updateEmitter.emit();
  }

  protected extraInfoChange(value) {
    this.inputProject.extraInfoInvoice = value;
    this.updateEmitter.emit();
  }

  protected setNoteForCustomer(value) {
    this.inputProject.quotation.noteForCustomer = value;
    this.updateEmitter.emit();
  }

  protected setQuotationSent(value) {
    this.inputProject.quotationSent = value;
    this.updateEmitter.emit();
  }

  protected setQuotationFollowed(value) {
    this.inputProject.quotationFollowed = value;
    this.updateEmitter.emit();
  }

  protected setQuotationApproved(value) {
    this.inputProject.quotation.approved = value;
    this.updateEmitter.emit();
  }

  protected setQuotationApprovalReasons(value) {
    this.inputProject.quotation.approvalReasons = value;
    this.updateEmitter.emit();
  }

  hasNoInvoices(): boolean {
    let result = [];
    if (this.inputProject.projectInvoices != null) {
      for (let invoice of this.inputProject.projectInvoices) {
        if (invoice.type === 'PlacementInvoice') {
          result.push(invoice);
        }
      }
    }
    if (result.length > 0) {
      return false;
    } else {
      return true;
    }
  }

  getSendButtonLabel() {
    return InvoiceHelper.getSendButtonLabel(
      this.project.projectContacts == null
        ? 0
        : this.project.projectContacts.length,
      this.getMatchingInvoices()
    );
  }

  getLinkText(invoice: ProjectInvoice) {
    return InvoiceHelper.getLinkText(invoice);
  }

  getAsBaseProject(subproject: SubProject): BaseProject {
    return <BaseProject>this.inputProject;
  }

  displayInstallerFeedback(product: Product) {
    if (
      product == null ||
      product.productPlacement == null ||
      StringHelper.isEmpty(product.productPlacement.installerFeedback)
    ) {
      return 'Geen informatie beschikbaar';
    }
    return product.productPlacement.installerFeedback;
  }

  getSendQuotationLabel() {
    return this.inputProject.quotation != null &&
      CollectionHelper.isPopulated(
        this.inputProject.quotation.registeredActions
      )
      ? 'Offerte opnieuw versturen'
      : 'Offerte versturen';
  }

  openSendQuotationModal() {
    let config = {
      project: this.project,
      subProject: this.inputProject,
    };
    this.dialogRef = this.dialog.open(SendQuotationMailComponent, {
      data: config,
    });
    this.addSubscription(
      this.dialogRef.afterClosed(),
      (registeredAction: RegisteredAction) => {
        if (registeredAction != null) {
          if (this.inputProject.quotation.registeredActions == null) {
            this.inputProject.quotation.registeredActions = [registeredAction];
          } else {
            this.inputProject.quotation.registeredActions.unshift(
              registeredAction
            );
          }
          this.inputProject.quotationSent = true;
        }
      }
    );
  }

  compareEntities(o1, o2) {
    return GeneralHelper.compareEntities(o1, o2);
  }

  isTestEnvironment(): boolean {
    return !globals.production;
  }
}
